import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { HomePageTileSubjectMatter, HomePageTileType, HomeTile, TileItem } from '@features/home/models/home.model';
import { HomeService } from '@features/home/services/home.service';
import { inject } from '@angular/core';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { pipe, switchMap, tap } from 'rxjs';
import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { IResponse } from '@shared/models/base/http-response.model';
import { HomePageResponse } from '@shared/communication/homepage-response';
import { ResponseStatus } from '@shared/models/base/response-status.model';
import { Favorite, FavoriteInfo } from '@features/favorites/models/favorites.model';
import { WhatsNewChangeType } from '@features/whats-new/models/whats-new.model';
import { Tile } from '../models/tile.model';

export interface HomeState {
  tiles: HomeTile[];
  pinned: HomeTile[];
  unpinned: HomeTile[];
  pinnedNames: string[];
  loading: boolean;
  status: {
    updateHomePageStatus: ResponseStatus;
  };
}

const initialState: HomeState = {
  tiles: [],
  pinned: [],
  unpinned: [],
  pinnedNames: [],
  loading: false,
  status: {
    updateHomePageStatus: ResponseStatus.IDLE,
  },
};

export const HomeStore = signalStore(
  { providedIn: 'root' },
  withDevtools('home'),
  withState(initialState),
  withMethods((store) => ({
    updatePinnedOrder(homeTiles: HomeTile[]) {
      homeTiles = homeTiles.map((tile) => {
        const homeTile: HomeTile = {
          ...tile,
          isPinned: true,
        };
        return homeTile;
      });
      patchState(store, {
        pinned: [...homeTiles],
        loading: false,
        status: { ...store.status(), updateHomePageStatus: ResponseStatus.LOADED },
      });
    },
  })),
  withMethods((store) => ({
    updateUnpinnedOrder(homeTiles: HomeTile[]) {
      homeTiles = homeTiles.map((tile) => {
        const homeTile: HomeTile = {
          ...tile,
          isPinned: false,
        };
        return homeTile;
      });
      patchState(store, {
        unpinned: [...homeTiles],
        loading: false,
        status: { ...store.status(), updateHomePageStatus: ResponseStatus.LOADED },
      });
    },
  })),
  withMethods((store) => ({
    pinItem: (item: HomeTile) => {
      item.isPinned = true;

      patchState(store, {
        pinned: [...store.pinned(), item],
        unpinned: store.unpinned().filter((t) => t.isPinned == false),
        loading: false,
        status: { ...store.status(), updateHomePageStatus: ResponseStatus.LOADED },
      });
    },
  })),
  withMethods((store) => ({
    unpinItem: (item: HomeTile) => {
      item.isPinned = false;
      patchState(store, {
        unpinned: [...store.unpinned(), item],
        pinned: store.pinned().filter((t) => t.isPinned == true),
        loading: false,
        status: { ...store.status(), updateHomePageStatus: ResponseStatus.LOADED },
      });
    },
  })),
  withMethods((store) => ({
    addToFavoriteTile: (favoriteInfo: Favorite) => {
      patchState(store, {
        loading: true,
        status: { ...store.status(), updateHomePageStatus: ResponseStatus.LOADING },
      });

      const tileItem: TileItem = {
        id: '',
        tileType: 2,
        subjectMatter: HomePageTileSubjectMatter.FAVORITE,
        itemPrefix: favoriteInfo.articleNumber!,
        description: favoriteInfo.articleHeading!,
        enabled: true,
      };

      const tiles = store.tiles();
      const favHomePageTileIndex = tiles.findIndex((item) => item.tileType == HomePageTileType.Favorite);
      if (favHomePageTileIndex) {
        tiles[favHomePageTileIndex].items[0].push(tileItem);
      }

      patchState(store, {
        tiles: tiles,
        loading: false,
        status: { ...store.status(), updateHomePageStatus: ResponseStatus.LOADED },
      });
    },
  })),
  withMethods((store) => ({
    updateFavoritesTile: (favoriteInfo: FavoriteInfo[]) => {
      patchState(store, {
        loading: true,
        status: { ...store.status(), updateHomePageStatus: ResponseStatus.LOADING },
      });

      const tileItems = favoriteInfo.map((info) => {
        const tileItem: TileItem = {
          id: '',
          tileType: 2,
          subjectMatter: HomePageTileSubjectMatter.NONE,
          itemPrefix: info.articleNumber,
          description: info.articleHeading!,
          enabled: true,
        };
        return tileItem;
      });

      let tiles = store.tiles();
      const favHomePageTileIndex = tiles.findIndex((item) => item.tileType == HomePageTileType.Favorite);

      if (favHomePageTileIndex !== -1) {
        tiles = tiles.map((tile, index) => {
          if (index === favHomePageTileIndex) {
            tile.items[0] = tileItems;
            return {
              ...tile,
            };
          }
          return tile;
        });
      }

      patchState(store, {
        tiles: tiles,
        loading: false,
        status: { ...store.status(), updateHomePageStatus: ResponseStatus.LOADED },
      });
    },
  })),
  withMethods((store, homeService = inject(HomeService)) => ({
    loadHomeData: rxMethod<void>(
      pipe(
        tap(() => patchState(store, { loading: true })),
        switchMap(() => {
          return homeService.getHomeData().pipe(
            tapResponse({
              next: (resp: IResponse<HomePageResponse>) => {
                let homeTiles: HomeTile[] = [];
                if (resp.success && resp.data) {
                  homeTiles = resp.data.tiles.map((tile) => {
                    const homeTile: HomeTile = {
                      ...tile,
                      items: [],
                    };
                    return homeTile;
                  });

                  const returnedTileItems = resp.data.cards.map((card) => {
                    const tileItem: TileItem = {
                      ...card,
                      id: '',
                      tileType: card.tileType,
                      changeType: card.changeType ?? WhatsNewChangeType.None,
                      enabled: card.changeType !== WhatsNewChangeType.Cancelled,
                    };
                    return tileItem;
                  });

                  const groupedItems: Record<string, Record<string, TileItem[]>> = {};

                  returnedTileItems.forEach(item => {
                    if (item.tileSubType != null) {

                      if (!groupedItems[item.tileType]) {
                        groupedItems[item.tileType] = {};
                      }

                      // Initialize the subType array if it doesn't exist
                      if (!groupedItems[item.tileType][item.tileSubType]) {
                        groupedItems[item.tileType][item.tileSubType] = [];
                      }

                      // Add the item to its group
                      groupedItems[item.tileType][item.tileSubType].push(item);
                    }
                  });

                  homeTiles.forEach(tile => {
                    if ([HomePageTileType.Comment, HomePageTileType.Synonym].includes(
                      tile.tileType
                    )) {
                      const tileItems = returnedTileItems.filter((item) => item.tileType == tile.tileType);
                      tile.items = [tileItems];
                    }
                    else {
                      const itemsByType = groupedItems[tile.tileType];

                      if (itemsByType) {
                        // Get all items for this tile type, organized by subType
                        const itemArrays = Object.values(itemsByType);

                        // Assign all item arrays to the tile's items
                        tile.items = itemArrays;
                      }
                    }
                  })
                }

                const pinned = homeTiles.filter((tile) => tile.isPinned == true);
                const unpinned = homeTiles.filter((tile) => tile.isPinned == false);
                patchState(store, {
                  tiles: homeTiles,
                  pinned,
                  unpinned,
                  loading: false,
                });
              },
              error: (err) => {
                patchState(store, {
                  tiles: [],
                  pinned: [],
                  unpinned: [],
                  loading: false,
                });
                console.error(err);
              },
            }),
          );
        }),
      ),
    ),
    updateHomePageTiles: rxMethod<void>(
      pipe(
        tap(() => {
          patchState(store, {
            loading: true,
            status: { ...store.status(), updateHomePageStatus: ResponseStatus.LOADING },
          });
        }),
        switchMap(() => {
          const pinnedTiles = store.pinned();
          const unPinnedTiles = store.unpinned();
          const allTiles = [...pinnedTiles, ...unPinnedTiles];
          return homeService.setUserTiles(allTiles.map(({ items, ...tile }) => tile)).pipe(
            tapResponse({
              next: (resp: IResponse<Tile[]>) => {
                // Transform here
                if (resp.success && resp.data) {
                  let homeTiles = resp.data.map((tile) => {
                    const matching = allTiles.find(t => t.name === tile.name);
                    const homeTile: HomeTile = {
                      ...tile,
                      items: matching ? matching.items : [],
                    };
                    return homeTile;
                  });

                  const pinned = homeTiles.filter((tile) => tile.isPinned == true);
                  const unpinned = homeTiles.filter((tile) => tile.isPinned == false);

                  patchState(store, {
                    loading: false,
                    pinned: pinned,
                    unpinned: unpinned,
                    status: { ...store.status(), updateHomePageStatus: ResponseStatus.LOADED },
                  });
                }
              },
              error: (err) => {
                patchState(store, initialState);
                console.error(err);
              },
            }),
          );
        }),
      ),
    ),
  })),
);
